<template>
    <!-- Panel for Run report -->
    <slide-over-panel   v-model:show="showHelpPanel">
        <template   #title>
            Help
        </template>

        <template   #content>
            This is the configuration and running page for reports.  On the table "Report Templates" sorted by
            group.  Each report template describes a group of settings for the report, that will be sent to all the
            members of that group.

            There are actions that can be taken on a report template.

            <h3     class="text-left">Run for me</h3>
            Run the report w/ the template's settings for just you.

            <h3     class="text-left">Run for group</h3>
            Run the report w/ the template's settings for all members of the group.

            <h3     class="text-left">Edit report</h3>
            Edit the report template's settings.

            <h3     class="text-left">Archive template</h3>
            Archive the template to keep the template from activating in the future.
            All reports that were previously run with this template will continue to be available in the Report History page.

            <slot   name="report-help-text"></slot>
        </template>
    </slide-over-panel>

    <!-- Panel for Create Report -->
    <slide-over-panel   v-model:show="showCreateTemplatePanel">
        <template   #title>
            Create Report Template on {{ groupName }}
        </template>

        <template   #content>
            <!-- Template Name -->
            <div class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                <span>Template Name</span>
                <input      class="py-1 px-3 leading-normal rounded text-base border rounded"
                            type="text"
                            v-model="$data._templateName">
            </div>

            <slot   name="create-settings">
            </slot>

            <!-- Create Template Button -->
            <!-- TODO. Fix Permission -->
            <div    v-if="canEdit"
                    class="flex justify-center">
                <button v-if="isSaving"
                        class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white rounded opacity-50 cursor-not-allowed">
                    <span class="lnr icon-loading animate-spin mr-3"></span>
                    Saving Settings, Please Wait
                </button>
                <button v-else
                        class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white hover:bg-blue-600 hover:text-white
                            focus:outline-none focus:ring-blue-200 focus:border-blue-400 active:bg-blue-600"
                        @click="createSettings">
                    Save Settings
                </button>
            </div>
        </template>
    </slide-over-panel>

    <!-- Panel for Edit Report -->
    <slide-over-panel   v-model:show="showEditTemplatePanel">
        <template   #title>
            Edit Report Template on {{ groupName }}
        </template>

        <template   #content>
            <!-- Template Name -->
            <div class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                <span>Template Name</span>
                <floating-input     v-model:value="$data._templateName"
                                    :readonly="isSavingDefaultTemplate"
                                    class="text-left"
                                    label="Template Name"
                ></floating-input>
            </div>

            <slot   name="edit-settings">
            </slot>

            <!-- Subscribed Users -->
            <div    v-if="isSavingDefaultTemplate && this.reportName !== 'nabp-report'"
                    class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                <span>Users to Send to Automatically</span>
                <select2
                    v-model:value="$data._settings['subscribedUsers']"
                    :multiple="true"
                    :options="users"
                ></select2>
            </div>

            <!-- Save Template Button -->
            <!-- TODO. Fix Permission -->
            <div    v-if="canEdit"
                    class="flex justify-center pb-5">
                <button v-if="isSaving"
                        class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white rounded opacity-50 cursor-not-allowed">
                    <span class="lnr icon-loading animate-spin mr-3"></span>
                    Saving Settings, Please Wait
                </button>
                <button v-else
                        class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white hover:bg-blue-600 hover:text-white
                            focus:outline-none focus:ring-blue-200 focus:border-blue-400 active:bg-blue-600"
                        @click="saveSettings($data._currentReportTemplateIndex)">
                    Save Settings
                </button>
            </div>

            <!-- List of Users in Group (Don't show for Legacy Report Template) -->
            <div    v-if="$data._groupId !== 0 && numberOfUsersOnGroup($data._groupId) > 0"
                    class="pb-5">
                <div   class="text-center text-lg text-gray-600">Users on Group</div>

                <!-- List of users -->
                <div    class="flex flex-wrap">
                    <div    v-for="user in $data._usersByGroup[$data._groupId]"
                            class="flex items-start">
                        <span class="inline-flex items-center m-1 px-2.5 py-0.5 rounded-md text-md font-medium bg-blue-100 text-blue-800">
                            {{ user }}
                        </span>
                    </div>
                </div>
            </div>
        </template>
    </slide-over-panel>

    <!-- Panel for Archive Report -->
    <slide-over-panel   v-model:show="showArchiveTemplatePanel">
        <template   #title>
            Archive Report Template: {{ $data._templateName }}
        </template>

        <template   #content>
            <div class="rounded-md bg-red-50 p-4">
                <div class="flex">
                    <div class="flex-shrink-0">
                        <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="ml-3">
                        <span class="text-sm font-medium text-red-800">Archiving Template</span>
                        <div class="mt-2 text-lg red">
                            <p>
                                Archiving this template will <b>permanently</b> remove it from the list of available templates. You will be able to view the previously run
                                reports on the Report History page.
                            </p>
                        </div>
                    </div>
                </div>
            </div>


            <!-- Save Template Button -->
            <div    v-if="canEdit"
                    class="flex justify-center">
                <button v-if="isSaving"
                        class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-red-200 text-md leading-5 font-sm
                            font-sans rounded-md text-red-700 bg-white rounded opacity-50 cursor-not-allowed">
                    <span class="lnr icon-loading animate-spin mr-3"></span>
                    Archiving Report Template, Please Wait
                </button>
                <button v-else
                        class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-red-200 text-md leading-5 font-sm
                            font-sans rounded-md text-red-700 bg-white hover:bg-red-600 hover:text-white
                            focus:outline-none focus:ring-red-200 focus:border-red-400 active:bg-red-600"
                        @click="archiveReport($data._currentReportTemplateIndex)">
                    Archive Report Template
                </button>
            </div>
        </template>
    </slide-over-panel>

    <!-- Panel for Run report -->
    <slide-over-panel   v-model:show="showIntervalSelectionPanel">
        <template   #title>
            Select Report Interval
        </template>

        <template   #content>
            <div    v-if="showIntervalSelectionPanel">
                <slot   :group-id="$data._groupId"
                        name="run-options"
                ></slot>
            </div>

            <div    v-if="$data._forGroup"
                    class="text-gray-400 flex justify-around w-full">
                <span   class="italic">Report will be sent to all members of group.</span>
            </div>
        </template>
    </slide-over-panel>

    <!-- Notification Modal for report running -->
    <notification-modal     v-model:is-open="$data._runningReport"
                            :show-icon="false"
                            :z-index="'z-10'">
        <template   #title-content>
            Submitting Report...
        </template>

        <template   #icon>
            <div    class="w-full flex justify-around">
                <span   :class="iconLoading"></span>
            </div>
        </template>

        <span></span>
    </notification-modal>

    <!-- Notification Modal for report ran -->
    <notification-modal     v-model:is-open="$data._ranReport"
                            :z-index="'z-10'">
        <template   #title-content>
            Report Processing
        </template>

        Your on-demand report has been submitted for creation and should be in your email inbox shortly. You'll also be able to see it on the Report History page.
        <template #button-primary-content>Sounds Good!</template>
    </notification-modal>

    <!-- Notification Modal for Standard Settings Saved -->
    <notification-modal     v-model:is-open="$data._standardSettingsSaved"
                            :z-index="'z-10'">
        <template   #title-content>
            Report settings saved successfully.
        </template>

        <span></span>
    </notification-modal>

    <!-- Notification Modal for Error's Running Report -->
    <notification-modal     v-model:is-open="$data._hasRunningErrors"
                            :is-error-modal="true"
                            :z-index="'z-10'">
        <template   #title-content>
            Errors occurred running the report.
        </template>

        <div    v-for="error in $data._runningErrors"
                class="">
            <span   class="my-1">
                {{ error }}
            </span>
        </div>
    </notification-modal>

    <div class="w-full ">
        <div>
            <!-- Back Button -->
            <div    v-if="groupReportingEnabled && displaySection !== null"
                    class="w-1/6">
                <button-blue-secondary      @click="clearSelection">
                    Return To Selection
                </button-blue-secondary>
            </div>

            <!-- Report Name -->
            <div    v-if="showDisplaySectionManual">
                <h2>{{ prettyReportName }}</h2>
            </div>
        </div>

        <!-- Modal to Select Manual vs Template -->
        <div    v-if="groupReportingEnabled && displaySection === null">
            <report-home-modal      :report-name="prettyReportName"
                                    @primary="primarySelection"
                                    @secondary="secondarySelection"
            ></report-home-modal>
        </div>

        <!-- Manual Run -->
        <div    v-if="displaySection === 'show-manual'"
                class="">
            <base-report>
                <!-- Left Column -->
                <template   #left-column>
                    <div>
                        <slot   :group-id="$data._groupId"
                                name="run-options"
                        ></slot>
                    </div>
                </template>

                <!-- Right Column -->
                <template   #right-column>
                    <slot   name="edit-settings">
                    </slot>

                    <!-- Save Template Button -->
                    <!-- TODO. Fix Permission -->
                    <div    v-if="! groupReportingEnabled && canManageExcursionReport && (this.reportName !== 'nabp-report' || this.reportName !== 'mkt-report')"
                            class="flex flex-col justify-center">
                        <!-- Subscribed Users -->
                        <div    class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                            <span>Users to Send to Automatically</span>
                            <select2
                                v-model:value="$data._settings['subscribedUsers']"
                                :multiple="true"
                                :options="users"
                            ></select2>
                        </div>

                        <div class="w-full flex justify-center">
                            <!-- Saving settings indicator -->
                            <button v-if="isSaving"
                                    class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white rounded opacity-50 cursor-not-allowed">
                                <span class="lnr icon-loading animate-spin mr-3"></span>
                                Saving Settings, Please Wait
                            </button>

                            <!-- Save Settings Button -->
                            <button v-else
                                    class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white hover:bg-blue-600 hover:text-white
                            focus:outline-none focus:ring-blue-200 focus:border-blue-400 active:bg-blue-600"
                                    @click="saveSettings($data._currentReportTemplateIndex)">
                                Save Settings
                            </button>
                        </div>
                    </div>
                </template>
            </base-report>
        </div>

        <!-- Template Page -->
        <div    v-if="groupReportingEnabled && displaySection === 'show-template'"
                class="flex justify-center w-full">
            <div    class="w-3/4">
                <h2> Saved Report Templates for {{ this.prettyReportName }}</h2>
                <ajax-table     v-model:items-per-page="ajaxTable.itemsPerPage"
                                v-model:loading="ajaxTable.loading"
                                v-model:page="ajaxTable.page"
                                v-model:page-info="ajaxTable.pageInfo"
                                v-model:search="ajaxTable.search"
                                v-model:sort-by="ajaxTable.sortBy"
                                v-model:sort-desc="ajaxTable.sortDesc"
                                v-model:selected-row="$data._currentReportTemplateIndex"
                                :row-selectable="false"
                                :allow-highlighted-rows="true"
                                :use-group-table-styling="true"
                                :data="$data._data"
                                :exportable="false"
                                :headers="headers"
                                @loadData="loadTemplateData"
                >
                    <!-- Help Button -->
                    <template   #search-right="props">
                        <div    class="flex items-center ml-2 mb-4 p-1 rounded-full border border-blue-700 cursor-pointer"
                                @click="showHelpPanel = true">
                            <span   class="lnr icon-question text-blue-700"></span>
                        </div>
                    </template>

                    <!-- Group Row -->
                    <template   #group-row="props">
                        <div    class="w-full flex justify-between items-center">
                            <div    class="px-6 py-4 w-1/2 font-bold flex items-center"
                            >
                                <span   class="px-3 py-1 rounded-full bg-green-200">
                                    {{ props.row['groupName'] }}
                                </span>

                                <div    v-if="! props.row.canView"
                                        class="ml-4 py-1">
                                    <tooltip-container  :tooltip-text="`You can't normally view this group, so you won't receive reports from these report templates.`"
                                                        width="250">
                                        <span   class="lnr icon-eye-crossed text-red-700"></span>
                                    </tooltip-container>
                                </div>

                            </div>
                            <div    v-if="canEdit && props.row.groupId !== 0"
                                    class="w-1/4 mr-40">
                                <button-green-primary @click="createReportTemplate($data._data[props.rowIndex])" class="my-2">
                                    Create new Template for {{ props.row['groupName'] }}
                                </button-green-primary>
                            </div>
                        </div>
                    </template>

                    <!-- Interval -->
                    <template   #col-2="props"
                                @click.stop>
                        <span>{{ formatInterval(props.row.report_duration_type) }}</span>
                        <span   v-if="props.row.report_duration_type === 'weekly'"
                                class="pl-1">
                            ({{ weekdayName(props.row.report_settings.weekday) }})
                        </span>
                    </template>

                    <!-- Actions to take on the report template. -->
                    <template   #col-3="props">
                        <div    class="w-full flex justify-end overflow-visible"
                                @click.stop>
                            <!-- Headless UI -->
                            <Menu as="div" class="inline-block text-left">
                                <div>
                                    <MenuButton class="inline-flex w-full justify-end rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                                        Action
                                        <div    class="h-5 w-5 mx-2">
                                            <span   :class="iconSortDesc"></span>
                                        </div>
                                    </MenuButton>
                                </div>

                                <MenuItems class="absolute mt-2 w-56 divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                                           style="">
                                    <div class="py-1">
                                        <MenuItem v-slot="{ active }">
                                        <span   :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm']"
                                                @click="setReportTemplate(props.rowIndex)"
                                        >
                                            <span    class="h-6 w-6 mr-3">
                                                <span   class="lnr icon-chevron-right text-gray-400"></span>
                                            </span>
                                            Run for me
                                        </span>
                                        </MenuItem>
                                        <MenuItem   v-slot="{ active }">
                                            <span   :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm']"
                                                    @click="setReportTemplate(props.rowIndex, true)"
                                            >
                                                <span    class="h-6 w-6 mr-3">
                                                    <span   class="lnr icon-exit-right text-gray-400"></span>
                                                </span>
                                                Run for group
                                            </span>
                                        </MenuItem>
                                            <MenuItem   v-if="permissions.can('view_report_history')"
                                                        v-slot="{ active }">
                                            <span   :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm']"
                                                    @click="viewReportHistory(props.rowIndex)"
                                            >
                                                <span    class="h-6 w-6 mr-3">
                                                    <span   class="lnr icon-history text-gray-400"></span>
                                                </span>
                                                View history
                                            </span>
                                        </MenuItem>
                                        <MenuItem   v-if="permissions.can('edit_report_templates')"
                                                    v-slot="{ active }">
                                        <span   :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm']"
                                                @click="editReportTemplate(props.rowIndex)">
                                            <span    class="h-6 w-6 mr-3">
                                                <span   class="lnr icon-clipboard-pencil text-gray-400"></span>
                                            </span>
                                            Edit report
                                        </span>
                                        </MenuItem>
                                        <MenuItem   v-if="permissions.can('delete_report_templates')"
                                                    v-slot="{ active }">
                                        <span   :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm']"
                                                @click="retireReportTemplate(props.rowIndex)">
                                            <span    class="h-6 w-6 mr-3">
                                                <span   class="lnr icon-drawers text-gray-400"></span>
                                            </span>
                                            Archive report
                                        </span>
                                        </MenuItem>
                                    </div>
                                </MenuItems>
                            </Menu>
                        </div>
                    </template>
                </ajax-table>

                <div    class="mb-16"></div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios"
import {SonicuCoreMixin} from "../../mixins/SonicuCoreMixin";
import {ajaxTableMixin} from "../../mixins/ajaxTableMixin";
import SlideOverPanel from "../elements/SlideOverPanel";
import Select2 from "../elements/select2";
import ButtonBlueSecondary from "../elements/buttons/ButtonBlueSecondary";
import ReportHomeModal from "./ReportHomeModal";
import BaseReport from "../Reports/BaseReport";
import AjaxTable from "../elements/AjaxTable";
import ButtonGreenPrimary from "../elements/buttons/ButtonGreenPrimary";
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/vue";
import {Intervals} from "./Intervals";
import {iconNamesMixin} from "../../mixins/IconNamesMixin";
import NotificationModal from "../elements/NotificationModal";
import {Features} from "../../mixins/AuthorizationHelpers/Features";
import _ from "lodash";
import FloatingInput from "../elements/FloatingInput.vue";
import TooltipContainer from "@/components/elements/TooltipContainer.vue";

const SHOW_MANUAL = 'show-manual'
const SHOW_TEMPLATE = 'show-template'

const REPORT_TEMPLATE_NAMES = {
    'detailed-alarm-report': 'Detailed Alarm Report',
    'alarm-resolution-report': 'Alarm Resolution Report',
    'excursion-report' : 'Excursion Report',
    'room-condition-report' : 'Room Condition Report',
    'nabp-report': 'MKT Report',
    'mkt-report': 'MKT Report',
    'weekly-device-summary-report': "Device Summary",
    'threshold-achievement-report': 'Threshold Achievement Report',
    'system-health-report': 'System Health Report',
    'occupancy-report': 'Occupancy Report',
}

export default {
    name: "GroupReportTemplate",
    mixins: [SonicuCoreMixin, ajaxTableMixin, iconNamesMixin],
    emit: ['update:runningReport', 'update:runningErrors', 'update:displaySection', 'useStandardReporting', 'resetSettings'],
    components: {
        TooltipContainer,
        FloatingInput,
        NotificationModal,
        Select2,
        MenuItem,
        MenuItems,
        Menu,
        MenuButton,
        ButtonGreenPrimary, AjaxTable, BaseReport, ReportHomeModal, ButtonBlueSecondary, SlideOverPanel},
    props: {
        interval: {
            type: String,
            default: 'monthly',
        },
        settings: {
            type: Object,
            default() {
                return {}
            }
        },
        meters: {
            type: Object,
            default() {
                return {}
            },
        },
        forGroup: {
            type: Boolean,
            default: false,
        },
        reportName: {
            type: String,
            default: 'detailed-alarm-report',
        },
        reloadData: {
            type: Boolean,
            default: false,
        },
        runningReport: {
            type: Boolean,
            default: false,
        },
        ranReport: {
            type: Boolean,
            default: false,
        },
        runningErrors: {
            type: Array,
            default() {
                return []
            },
        },
        currentReportTemplateId: {
            type: Number,
            default: 0,
        },
        defaultSettings: {
            type: Object,
            default() {
                return {}
            },
        },
        prepareSettingsFunction: {
            type: Function,
            default: null,
        },
        canEdit: {
            type: Boolean,
            default: false,
        },
        users: {
            // I am leaving this blank on the blade as well because
            // there is probably going to be some decent logic to it
            type: Object,
            default() {
                return {}
            },
        },
        displaySection: {
            type: [String, null],
            default: null
        },
    },
    data() {
        return {
            _displaySection: this.displaySection,

            // Manual Run Stuff
            _interval: this.interval,
            endOfInterval: new Date(),
            _selectedMeters: [],
            _templateName: '',
            _groupId: 0,
            _settings: this.defaultSettings,

            // Template Run Stuff
            headers: [
                {
                    name: 'Report Template',
                    key: 'template_name',
                    sortBy: 'reportTemplate',
                },
                {
                    name: 'Send Interval',
                    key: 'report_duration_type',
                    sortBy: 'sendInterval',
                },
                // TODO. May cause errors.
                {
                    name: '',
                    key: 'created_at',
                },
            ],
            _data: [],
            _groups: [],
            _usersByGroup: {},
            isSaving: false,

            _runningReport: this.runningReport,
            _ranReport: this.ranReport,
            _hasRunningErrors: this.runningErrors > 0,
            _runningErrors: this.runningErrors,

            _currentReportTemplateIndex: null,
            _currentReportTemplateId: this.currentReportTemplateId,
            showHelpPanel: false,
            showIntervalSelectionPanel: false,
            showCreateTemplatePanel: false,
            showEditTemplatePanel: false,
            showArchiveTemplatePanel: false,
            _forGroup: this.forGroup,
            _standardSettingsSaved: false,
        }
    },
    created() {
    },
    mounted() {
        // Add listener to feature level helper to check for group report templates enabled.
        this.featureLevel.addListener('GroupReportTemplate.updateDisplaySection', company => {
            this.$data._displaySection = this.featureLevel.hasFeature(Features.GROUP_REPORTING)
                ? null
                : SHOW_MANUAL

            if (this.$data._displaySection === SHOW_MANUAL) {
                this.primarySelection()
                this.$emit('useStandardReporting', true)
            }

            this.$forceUpdate()
        })

        if (! _.has(this.$data._settings, 'subscribedUsers')) {
            console.log(`Clear Subscribed Users`)
            this.$data._settings['subscribedUsers'] = []
        }
    },
    methods: {
        loadTemplateData() {
            this.ajaxTable.loading = true
            // this.requestNumber++
            let params = JSON.parse(JSON.stringify(this.ajaxTable))
            params.report_name =  this.reportName

            axios.get(this.company.buildCompanyApiInternalRoute(`report-templates`), {
                params: params,
            }).then(response => {
                let data = response.data.data
                this.ajaxUpdateTableData(response.data)

                // For now we will process the data and insert new rows for the groups on the Front-End
                // This is the wrong solution in the long term, at least it shouldn't be done here.
                let newData = []

                // Add a fake group called "User-Specific (Non-Group) Reporting"
                newData.push({
                    groupId: 0,
                    groupName: 'User-Specific (Non-Group) Reporting',
                    canView: true,
                })

                // Add User-Specific (Non-Group) Reporting Template to table
                let defaultTemplate = response.data.defaultTemplate
                this.ensureReportTemplateHasDefaultSettings(defaultTemplate)
                newData.push(defaultTemplate)

                let lastGroupId = -1;
                let listedGroups = []
                _.forEach(data, (item, index) => {
                    // If we have moved to the next group, add a new group row
                    if (item.group_id > lastGroupId) {
                        newData.push({
                            groupId: item.group_id,
                            groupName: item.group_name,
                            canView: item.can_view,
                        })
                        lastGroupId = item.group_id
                        listedGroups.push(item.group_id)
                    }

                    // Make sure this report template has new default settings.
                    this.ensureReportTemplateHasDefaultSettings(item)
                    newData.push(item)
                })

                // Add Groups not included already, if there is no search
                let search = this.ajaxTable.search
                _.forEach(response.data.groups, (item, index) => {
                    if (listedGroups.indexOf(item.ID) === -1) {
                        if (search === '' || (item.GroupName.toLowerCase().indexOf(search.toLowerCase()) !== -1)) {
                            newData.push({
                                groupId: item.ID,
                                groupName: item.GroupName,
                                canView: item.can_view,
                            })
                        }
                    }
                })

                this.$data._data = newData
                this.$data._groups = response.data.groups
                this.$data._usersByGroup = response.data.usersByGroup
            })
            .catch(error => {
            })
            .finally(() => {

            })
        },
        createSettings() {
            console.log(`create Settings`)
            console.log(this.$data._settings)

            console.log(`Default Settings`)
            console.log(this.defaultSettings)
            let params = {
                groupId: this.$data._groupId,
                reportName: this.reportName,
                templateName: this.$data._templateName,
                interval: this.interval,
                settings: this.$data._settings,
            }

            this.isSaving = true
            axios.post(this.company.buildCompanyApiInternalRoute(`report-templates`), params)
                .then(response => {
                    let data = response.data
                })
                .catch(response => {
                    let data = response.data
                })
                .finally(() => {
                    this.resetTemplateRun()
                    this.loadTemplateData()
                })
        },
        saveSettings(reportTemplateIndex) {
            console.log(`Saving Settings on DAR file`)

            // Log Settings
            let template = this.$data._data[reportTemplateIndex]
            console.log(`Template:`)
            console.log(template)
            let params = null
            let route = null
            if (_.isNil(template)) {
                params = this.saveSettingsStandard()
            }
            else {
                params = this.saveSettingsReportTemplate(template)
                route = template.id
            }

            this.isSaving = true
            axios.put(this.company.buildCompanyApiInternalRoute(`report-templates/${route}`), params)
                .then(response => {
                    this.$data._standardSettingsSaved = true
                })
                .catch(response => {
                })
                .finally(() => {
                    this.resetTemplateRun()
                    this.loadTemplateData()
                    this.isSaving = false
                })
        },
        archiveReport(reportTemplateIndex) {
            console.log(`Archiving Report Template`)
            let template = this.$data._data[reportTemplateIndex]

            this.isSaving = true
            axios.delete(this.company.buildCompanyApiInternalRoute(`report-templates/${template.id}`))
                .then(response => {
                })
                .catch(response => {
                })
                .finally(() => {
                    this.resetTemplateRun()
                    this.loadTemplateData()
                    this.isSaving = false
                })
        },
        saveSettingsReportTemplate(reportTemplate) {
            return {
                group_report: true,
                template_name: this.$data._templateName,
                interval: this.$data._interval,
                settings: this.$data._settings,
            }
        },
        saveSettingsStandard() {
            // Generate parameters for a standard (non-group) report.
            return {
                group_report: false,
                report_name: this.reportName,
                interval: this.$data._interval,
                settings: this.$data._settings,
            }
        },
        viewReportHistory(reportTemplateIndex) {
            let reportTemplateId = this.$data._data[reportTemplateIndex].id
            window.open(this.company.buildCompanyRoute(`report-history/?templateID[]=${reportTemplateId}`))
        },
        setReportTemplate(reportTemplateIndex, forGroup = false) {
            this.$data._currentReportTemplateIndex = reportTemplateIndex
            this.$data._currentReportTemplateId = this.$data._data[reportTemplateIndex].id

            let template = this.$data._data[reportTemplateIndex]
            this.$data._templateName = template.template_name
            this.$data._interval = template.report_duration_type
            this.$data._groupId = template.group_id
            this.$data._forGroup = forGroup

            this.showIntervalSelectionPanel = true
        },
        editReportTemplate(reportTemplateIndex) {
            let template = this.$data._data[reportTemplateIndex]

            this.$data._templateName = template.template_name
            this.$data._interval = template.report_duration_type
            this.$data._groupId = template.group_id

            // Set the log times to be dates and set the modal's settings
            let settings = template.report_settings
            if (Array.isArray(settings) || settings === '[]') {
                settings = {}
            }

            this.$data._settings = this.prepareSettings(settings)

            this.$data._currentReportTemplateIndex = reportTemplateIndex
            this.showEditTemplatePanel = true
        },
        retireReportTemplate(reportTemplateIndex) {
            let template = this.$data._data[reportTemplateIndex]

            this.$data._templateName = template.template_name

            this.$data._currentReportTemplateIndex = reportTemplateIndex
            this.showArchiveTemplatePanel = true
        },
        createReportTemplate(groupRow) {
            this.$data._templateName = 'Default Template'
            this.$data._interval = 'monthly'
            this.$data._groupId = groupRow.groupId

            this.$data._settings = this.prepareSettings(this.defaultSettings)

            this.showCreateTemplatePanel = true
        },
        resetTemplateRun() {
            this.$data._currentReportTemplateIndex = null
            this.showIntervalSelectionPanel = false
            this.showEditTemplatePanel = false
            this.showArchiveTemplatePanel = false
            this.showCreateTemplatePanel = false
            this.isSaving = false
            this.$data._forGroup = false
        },
        ensureReportTemplateHasDefaultSettings(reportTemplate) {
            // Check if Settings Are empty and populate them w/ default settings if so.
            if (_.isEmpty(reportTemplate.report_settings)) {
                reportTemplate.report_settings = this.defaultSettings
            }

            // Check each setting w/ default settings and add if not present
            let missingSettings = _.difference(Object.keys(this.defaultSettings), Object.keys(reportTemplate.report_settings))
            _.forEach(missingSettings, (setting) => {
                reportTemplate.report_settings[setting] = this.defaultSettings[setting]
            })
        },
        primarySelection() {
            this._displaySection = SHOW_MANUAL
            this.$data._groupId = 0
            this.$emit('resetSettings')
            let settings = this.defaultSettings
            if (Array.isArray(settings) || settings === '[]') {
                settings = {}
            }

            this.$data._settings = this.prepareSettings(settings)

        },
        secondarySelection() {
            this._displaySection = SHOW_TEMPLATE
            this.$data._groupId = 0
            this.$emit('resetSettings')
        },
        clearSelection() {
            this._displaySection = null
        },
        prepareSettings(settings) {
            if (! _.isNil(this.prepareSettingsFunction)) {
                console.log(`use prepareSettingsFunction`)
                settings = this.prepareSettingsFunction(settings)
            }
            else if (_.isNil(settings)) {
                console.log(`use default Settings`)
                settings = this.defaultSettings
            }

            if (! this.isSavingDefaultTemplate) {
                console.log(`Clear subscribedUsers`)
                settings.subscribedUsers = []
            }

            return settings
        },
        formatInterval(interval) {
            return Intervals.INTERVAL_OPTIONS[interval]
        },
        weekdayName(weekday) {
            if (_.isNil(weekday)) {
                weekday = 1
            }
            return Intervals.WEEKDAY_OPTIONS[weekday]
        },
        numberOfUsersOnGroup(groupId) {
            return _.has(this.$data._usersByGroup, groupId)
                ? Object.keys(this.$data._usersByGroup[groupId]).length
                : 0
        },
    },
    computed: {
        groupName() {
            return _.has(this.$data._groups, this.$data._groupId)
                ? this.$data._groups[this.$data._groupId]['GroupName']
                : null
        },
        isSavingDefaultTemplate() {
            return this.$data._groupId === 0
        },
        prettyReportName() {
            return REPORT_TEMPLATE_NAMES[this.reportName]
        },
        canManageExcursionReport() {
            return this.canEdit
        },
        groupReportingEnabled() {
            return this.featureLevel.hasFeature(Features.GROUP_REPORTING)
        },
        showDisplaySelectionModal() {
            return this.$data._displaySection === null
        },
        showDisplaySectionTemplate() {
            return this.$data._displaySection === SHOW_TEMPLATE
        },
        showDisplaySectionManual() {
            return this.$data._displaySection === SHOW_MANUAL
        },
    },
    watch: {
        reloadData(val, old) {
            if (val === true && val !== old) {
                this.loadTemplateData()
                this.$emit('update:reloadData', false)
            }
        },
        _forGroup(val, old) {
            if (val !== old) {
                this.$emit('update:forGroup', this.$data._forGroup)
            }
        },
        showIntervalSelectionPanel(val, old) {
            // only reset the current template index if the panel closes (goes from true => false)
            if (old === true && val !== old) {
                this.$data._currentReportTemplateIndex = null
            }
        },
        showEditTemplatePanel(val, old) {
            // only reset the current template index if the panel closes (goes from true => false)
            if (old === true && val !== old) {
                this.$data._currentReportTemplateIndex = null
            }
        },
        showArchiveTemplatePanel(val, old) {
            // only reset the current template index if the panel closes (goes from true => false)
            if (old === true && val !== old) {
                this.$data._currentReportTemplateIndex = null
            }
        },
        runningReport(val, old) {
            if (val !== old) {
                this.$data._runningReport = val
            }
        },
        _runningReport(val, old) {
            if (val !== old) {
                this.$emit('update:runningReport', val)
            }
        },
        ranReport(val, old) {
            if (val !== old) {
                this.$data._ranReport = val
            }
        },
        _ranReport(val, old) {
            if (val !== old) {
                this.$emit('update:ranReport', val)
            }
        },
        runningErrors: {
            handler(val, old) {
                if (val !== old) {
                    this.$data._runningErrors = val
                    this.$data._hasRunningErrors = val.length > 0
                }
            },
            deep: true,
        },
        _runningErrors: {
            handler(val, old) {
                if (val !== old) {
                    this.$emit('update:runningErrors', val)
                }
            },
            deep: true,
        },
        currentReportTemplateId(val, old) {
            if (val !== old) {
                this.$data._currentReportTemplateId = val
            }
        },
        _currentReportTemplateId(val, old) {
            if (val !== old) {
                this.$emit('update:currentReportTemplateId', val)
                this.$emit('resetSettings')
            }
        },
        interval(val, old) {
            if (val !== old) {
                this.$data._interval = val
            }
        },
        _interval(val, old) {
            if (val !== old) {
                this.$emit('update:interval', val)
                if (this.$data._interval === 'semi-monthly') {

                }
            }
        },
        settings: {
            handler(val, old) {
                if (val !== old) {
                    this.$data._settings = val
                }
            },
            deep: true,
        },
        _settings: {
            handler(val, old) {
                if (val !== old) {
                    this.$emit('update:settings', val)
                }
            },
            deep: true,
        },
        _displaySection(val, old) {
            if (val !== old) {
                this.$emit('update:displaySection', this.$data._displaySection)
            }
        }
    },
}
</script>

<style scoped>

</style>